import _ from 'lodash'
import React, { useMemo } from 'react'

import { alpha, createTheme, useTheme, Theme, ThemeProvider, SxProps } from '@mui/material/styles'
import Checkbox from '@mui/material/Checkbox'
import { FormHelperTextProps } from '@mui/material/FormHelperText'
import { InputBaseProps } from '@mui/material/InputBase'
import { InputLabelProps } from '@mui/material/InputLabel'
import { InputProps as StandardInputProps } from '@mui/material/Input'
import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'

import colors from '@/assets/scss/_themes-vars.module.scss'

export type SelectOption = {
  label: string
  value: string | number
}

export type SelectInputProps = {
  id?: string
  /**
   * The default value.
   */
  defaultValue?: SelectOption['value']
  /**
   * If `true`, the component is disabled.
   * @default false
   */
  disabled?: boolean
  /**
   * If `true`, the menu option has the check box
   */
  enableCheckmark?: boolean
  /**
   * If `true`, the component is displayed in an error state.
   * @default false
   */
  error?: boolean
  /**
   * Props applied to the FormHelperText element.
   */
  FormHelperTextProps?: Partial<FormHelperTextProps>
  /**
   * If `true`, the input will take up the full width of its container.
   * @default false
   */
  fullWidth?: boolean
  /**
   * The helper text content.
   */
  helperText?: React.ReactNode
  IconComponent?: React.ElementType
  /**
   * Props applied to the Input element.
   * It will be a [`OutlinedInput`](https://mui.com/api/outlined-input/)
   */
  InputProps?: Partial<StandardInputProps>
  /**
   * Props applied to the [`InputLabel`](https://mui.com/api/input-label/) element.
   */
  InputLabelProps?: Partial<InputLabelProps>
  /**
   * [Attributes](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#Attributes) applied to the `input` element.
   */
  inputProps?: InputBaseProps['inputProps']
  /**
   * The label content.
   */
  label?: React.ReactNode
  multiple?: boolean
  /**
   * Name attribute of the `input` element.
   */
  name?: string
  /**
   * Callback fired when the input is blur.
   *
   * @param {object} event The event source of the callback.
   */
  onBlur?: InputBaseProps['onBlur']
  /**
   * Callback fired when the value is changed.
   *
   * @param {object} event The event source of the callback.
   * You can pull out the new value by accessing `event.target.value` (string).
   */
  onChange?: StandardInputProps['onChange']
  /**
   * Callback fired when the input is focused.
   *
   * @param {object} event The event source of the callback.
   */
  onFocus?: StandardInputProps['onFocus']
  /**
   * The short hint displayed in the `input` before the user enters a value.
   */
  placeholder?: string
  /**
   * If `true`, the label is displayed as required and the `input` element is required.
   * @default false
   */
  required?: boolean
  /**
   * The options of the component.
   * @type SelectionOption[]
   */
  options: SelectOption[]
  /**
   * It prevents the user from changing the value of the field
   * (not from interacting with the field).
   */
  readOnly?: boolean
  /**
   * @ignore
   */
  sx?: SxProps<Theme>
  /**
   * The input value. Providing an empty string will select no options.
   * Set to an empty string '' if you don't want any of the available options to be selected.
   */
  value?: any
}

const createSelectInputTheme = (defaultTheme: Theme) => {
  return createTheme(defaultTheme, {
    components: {
      MuiFormLabel: {
        styleOverrides: {
          root: {
            color: colors.gray700,
            lineHeight: '1.25rem',
            '& + .MuiOutlinedInput-root': {
              marginTop: 4,
            },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          outlined: {
            fontSize: '0.875rem',
            fontWeight: 500,
            transform: 'none',
            '& + .MuiOutlinedInput-root': {
              marginTop: 24,
            },
            '&.Mui-focused:not(.Mui-error)': {
              color: colors.gray700,
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            lineHeight: '1.25rem',
            boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05), 0px 0px 0px 2px #FFFFFF',
            '&&& .MuiInputBase-input': {
              // tripple '&' to override default behaviour
              '&::-webkit-input-placeholder': { opacity: '1 !important' },
              '&::-moz-placeholder': { opacity: '1 !important' }, // Firefox 19+
              '&:-ms-input-placeholder': { opacity: '1 !important' }, // IE11
              '&::-ms-input-placeholder': { opacity: '1 !important' }, // Edge
            },
            '&:hover:not(.Mui-disabled):not(.Mui-error)': {
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: colors.gray300,
              },
            },
            '&.Mui-focused': {
              '& .MuiOutlinedInput-notchedOutline': {
                borderWidth: 1,
                borderColor: colors.primaryMain?.startsWith('#') ? alpha(colors.primaryMain, 0.38) + '!important' : colors.primaryMain,
              },
            },
          },
          input: {
            fontSize: '14px',
            padding: '8px 12px',
            height: 'auto',
            border: '1px solid rgba(0,0,0,0)',
          },
          notchedOutline: {
            top: 0,
            borderColor: colors.gray300,
            '& legend': {
              display: 'none',
            },
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          input: {
            '&.Mui-disabled': {
              cursor: 'not-allowed',
            },
          },
        },
      },
    },
  })
}

export function SelectInput(props: SelectInputProps) {
  const {
    defaultValue = '',
    enableCheckmark = false,
    IconComponent,
    InputProps,
    InputLabelProps,
    multiple = false,
    name,
    options,
    placeholder,
    readOnly,
    value,
    ...rest
  } = props

  const defaultTheme = useTheme()
  const selectInputTheme = useMemo(() => createSelectInputTheme(defaultTheme), [defaultTheme])

  return (
    <ThemeProvider theme={selectInputTheme}>
      <TextField
        {...rest}
        defaultValue={defaultValue}
        InputProps={{
          ...InputProps,
          placeholder,
          readOnly,
        }}
        InputLabelProps={{
          ...InputLabelProps,
          shrink: true,
        }}
        name={name}
        select
        SelectProps={{
          displayEmpty: true,
          IconComponent,
          multiple,
          renderValue: (selected: any) => {
            if (placeholder && (!selected || (Array.isArray(selected) && selected.length === 0))) return <em>{placeholder}</em>

            if (Array.isArray(selected)) {
              return _.map(
                _.filter(options, (option) => _.includes(selected, option.value)),
                'label'
              ).join(', ')
            }
            return _.find(options, ['value', selected])?.label || ''
          },
        }}
        value={value}
      >
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {enableCheckmark && multiple && Array.isArray(value) && (
              <Checkbox checked={value.indexOf(option.value) > -1} sx={{ '& .MuiSvgIcon-root': { marginRight: '0 !important' } }} />
            )}
            {option.label}
          </MenuItem>
        ))}
      </TextField>
    </ThemeProvider>
  )
}

export default SelectInput
